import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileSignature, Plus, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { listTemplates, uploadTemplate } from '@/services/sign.service.js';

// "Sign with Tower" template manager. Lists signable-document templates and
// is the entry point to the field-mapping builder. Creating a template means
// uploading the source PDF; mapping its fields happens in the builder.

export default function SignAdmin() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState(null);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  async function load() {
    try {
      const { templates } = await listTemplates('active');
      setTemplates(templates || []);
    } catch (e) {
      setError(e);
    }
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-text">Sign with Tower</h2>
          <p className="mt-1 text-sm text-text-muted">Signable-document templates · map fields once, reuse for every signing</p>
        </div>
        <Button size="sm" onClick={() => setDialogOpen(true)}>
          <Plus size={15} strokeWidth={2} /> New template
        </Button>
      </div>

      <div className="mt-4">
        {!templates && !error && (
          <div className="rounded-card border border-border bg-surface p-6 text-sm text-text-muted">Loading…</div>
        )}
        {error && (
          <div className="rounded-card border border-accent/40 bg-accent-soft p-4 text-sm text-accent">
            Couldn't load templates: {error.body?.error || error.code || error.message}
          </div>
        )}
        {templates && templates.length === 0 && (
          <div className="rounded-card border border-dashed border-border p-8 text-center text-sm text-text-muted">
            No templates yet. Upload a PDF to map its signature, date, and text fields.
          </div>
        )}
        {templates && templates.length > 0 && (
          <div className="overflow-hidden rounded-card border border-border bg-surface">
            <div className="grid grid-cols-[1.6fr_8rem_5rem_6rem] gap-2 border-b border-border bg-surface-strong/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-text-dim">
              <span>Name</span><span>Type</span><span>Pages</span><span>Status</span>
            </div>
            {templates.map((t) => (
              <div key={t.id} role="button" tabIndex={0}
                onClick={() => navigate(`/admin/sign/${t.id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/admin/sign/${t.id}`); }}
                className="grid w-full cursor-pointer grid-cols-[1.6fr_8rem_5rem_6rem] items-center gap-2 border-b border-border px-3 py-2.5 text-left text-sm last:border-0 hover:bg-surface-strong/50">
                <span className="flex items-center gap-2 truncate font-medium text-text">
                  <FileSignature size={15} strokeWidth={1.5} className="text-text-dim" />
                  {t.name}
                </span>
                <span className="text-text-muted">{t.doc_type || '—'}</span>
                <span className="text-text-dim">{Array.isArray(t.page_sizes) ? t.page_sizes.length : '—'}</span>
                <span><Badge variant={t.status === 'active' ? 'success' : 'dashed'}>{t.status}</Badge></span>
              </div>
            ))}
          </div>
        )}
      </div>

      <NewTemplateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={(tpl) => { setDialogOpen(false); navigate(`/admin/sign/${tpl.id}`); }}
      />
    </div>
  );
}

function NewTemplateDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState('');
  const [docType, setDocType] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  function reset() { setName(''); setDocType(''); setFile(null); setBusy(false); }

  async function submit() {
    if (!file) { toast.error('Choose a PDF first'); return; }
    if (!name.trim()) { toast.error('Name the template'); return; }
    setBusy(true);
    try {
      const { template } = await uploadTemplate(file, { name: name.trim(), docType: docType.trim() });
      toast.success(`Template "${template.name}" created — map its fields`);
      reset();
      onCreated(template);
    } catch (e) {
      toast.error(e.message || 'Upload failed');
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { onOpenChange(v); if (!v) reset(); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New template</DialogTitle>
          <DialogDescription>Upload the source PDF. You'll map fields next.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-text-muted">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. TRIA Disclosure" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-text-muted">Type <span className="text-text-dim">(optional)</span></label>
            <Input value={docType} onChange={(e) => setDocType(e.target.value)} placeholder="tria, ca-bind, acord, nda…" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-text-muted">Source PDF</label>
            <label htmlFor="sign-tpl-file"
              className="flex cursor-pointer items-center gap-2 rounded-card border border-dashed border-border-strong px-3 py-3 text-sm text-text-muted hover:bg-border/20">
              <Upload size={15} strokeWidth={1.5} />
              {file ? <span className="truncate text-text">{file.name}</span> : 'Choose a PDF…'}
            </label>
            <input id="sign-tpl-file" type="file" accept="application/pdf" className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? <><Loader2 size={15} className="animate-spin" /> Uploading…</> : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
